import type {
    UploadedFileAttachment,
    DisplayMessage as AgentMsgDisplayType,
    ToolCallDetail,
    ToolResultDetail,
} from '@/core/models/medconnect-agent.model';
import { normalizeUtf8 } from '@/utils/encoding';

// ============================================================================
// Base64 Encoding/Decoding Utilities
// ============================================================================

/**
 * Safely encodes a UTF-8 string to Base64.
 * Handles Unicode characters properly.
 */
export const utf8ToBase64 = (str: string): string => {
    const bytes = new TextEncoder().encode(str);
    let binaryStr = '';
    bytes.forEach((byte) => (binaryStr += String.fromCharCode(byte)));
    return btoa(binaryStr);
};

/**
 * Safely decodes a Base64 string to UTF-8.
 * Handles Unicode characters properly.
 */
export const base64ToUtf8 = (base64: string): string => {
    const binaryStr = atob(base64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
};

// ============================================================================
// ID Generation
// ============================================================================

/**
 * Generates a unique ID for messages.
 */
export const generateMessageId = () =>
    `id-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// ============================================================================
// Message Mappers
// ============================================================================

/**
 * Maps an individual backend message to a display message.
 * Used for standalone messages that are not part of a complex AI sequence.
 */
export const mapIndividualBackendMessageToDisplayMessage = (backendMsg: any): AgentMsgDisplayType => {
    const messageType = backendMsg.type as AgentMsgDisplayType['type'];
    const content = typeof backendMsg.content === 'string'
        ? backendMsg.content
        : JSON.stringify(backendMsg.content ?? '');

    return {
        id: backendMsg._id?.toString() || generateMessageId(),
        type: messageType,
        content: content,
        timestamp: backendMsg.createdAt || backendMsg.updatedAt || new Date().toISOString(),
        createdAt: backendMsg.createdAt,
        updatedAt: backendMsg.updatedAt,
        attachedFiles:
            backendMsg.files?.map((f: any) => ({
                filename: f.filename,
                key: f.key,
                url: f.url || '',
                objectType: f.objectType,
            })) || undefined,
        tool_calls_details: messageType === 'ai' ? backendMsg.tool_calls_details : undefined,
        tool_name: messageType === 'tool' ? backendMsg.name : undefined,
        tool_call_id: messageType === 'tool' ? backendMsg.tool_call_id : undefined,
        structured_content: messageType === 'tool' ? backendMsg.structured_content : undefined,
    };
};

// ============================================================================
// Chat History Processing
// ============================================================================

/** Generic announcement patterns to filter out */
const GENERIC_TOOL_ANNOUNCEMENTS = [
    '[AI is using tools]',
    '[AI is preparing to use tools]',
    '[AI is using tools - reasoning not fully captured]',
];

/**
 * Checks if a message is a generic tool announcement that should be filtered.
 */
const isGenericToolAnnouncement = (content: string): boolean =>
    GENERIC_TOOL_ANNOUNCEMENTS.includes(content);

/**
 * Creates a Base64-encoded tool interaction tag.
 */
const createToolInteractionTag = (interaction: Partial<ToolResultDetail>): string => {
    const jsonString = JSON.stringify(interaction);
    const base64EncodedJson = utf8ToBase64(jsonString);
    return `[tool_interaction](${base64EncodedJson})`;
};

/**
 * Builds the inline `[tool_interaction](base64)` tags appended to the streaming AI message
 * when the server announces tool calls (results are filled in from the persisted history).
 */
export const buildToolInteractionTags = (
    calls: Array<{ id: string; name: string; args?: any }>
): string => {
    const tags = calls
        .map((tc) => createToolInteractionTag({ tool_call_id: tc.id, tool_name: tc.name, tool_args: tc.args || {}, result: undefined }))
        .join('\n\n');
    return `\n\n${tags}\n\n`;
};

/**
 * Finds and updates a tool interaction tag with a result.
 * Returns the index of the updated tag, or -1 if not found.
 */
const findAndUpdateToolTag = (
    elements: string[],
    toolCallId: string,
    existingInteraction: Partial<ToolResultDetail>
): number => {
    const tagToUpdateIndex = elements.findIndex((el) => {
        if (el.startsWith('[tool_interaction](')) {
            const base64Part = el.substring('[tool_interaction]('.length, el.length - 1);
            try {
                const decodedJson = base64ToUtf8(base64Part);
                const parsed = JSON.parse(decodedJson) as Partial<ToolResultDetail>;
                return parsed.tool_call_id === toolCallId && parsed.result === undefined;
            } catch (e) {
                console.error('Error decoding/parsing Base64 in findIndex:', e);
                return false;
            }
        }
        return false;
    });

    if (tagToUpdateIndex !== -1) {
        elements[tagToUpdateIndex] = createToolInteractionTag(existingInteraction);
    }

    return tagToUpdateIndex;
};

/** Start of the notes the backend appends to a user turn for the model (attached files, extracted image text). */
const SYSTEM_NOTE_MARKER = '\n\n[System:';

/**
 * The user's own words, without the `[System: …]` notes the backend appends to their turn for the model
 * (`prepareContextAndPrompt`). The notes are always appended at the end, so everything from the first one is cut.
 */
export const stripSystemNotes = (content: string): string => {
    const index = content.indexOf(SYSTEM_NOTE_MARKER);
    return index === -1 ? content : content.slice(0, index).trimEnd();
};

/**
 * Processes raw chat history from the backend to create display messages.
 * Combines AI reasoning, tool calls, tool results, and final AI answers into single blocks.
 * 
 * @param chatHistory - Raw chat history array from the backend
 * @param t - Translation function for localized strings
 * @returns Processed array of display messages
 */
export const processChatHistoryForDisplay = (
    chatHistory: any[],
    t: (key: string) => string
): AgentMsgDisplayType[] => {
    const displayMessages: AgentMsgDisplayType[] = [];
    let i = 0;

    while (i < chatHistory.length) {
        const currentMsgData = chatHistory[i];

        if (currentMsgData.type === 'ai') {
            const result = processAIMessageBlock(chatHistory, i, t);
            if (result.displayMessage) {
                displayMessages.push(result.displayMessage);
            }
            i += result.consumedCount > 0 ? result.consumedCount : 1;
        } else {
            const displayMessage = mapIndividualBackendMessageToDisplayMessage(currentMsgData);
            if (displayMessage.type === 'human' && typeof displayMessage.content === 'string') {
                displayMessage.content = stripSystemNotes(displayMessage.content);
            }
            displayMessages.push(displayMessage);
            i++;
        }
    }

    return displayMessages;
};

/**
 * Counts assistant messages with actual text content in a *display* message list.
 * Streaming placeholders (id starting with `streaming-`) are ignored.
 *
 * Used by the stream-recovery logic: when an SSE stream dies (mobile background tab,
 * proxy timeout) the server usually finishes the turn and persists the reply — comparing
 * this count before/after a history refetch tells whether the answer actually arrived.
 */
export const countAssistantDisplayMessages = (messages: Array<Pick<AgentMsgDisplayType, 'id' | 'type' | 'content'>>): number =>
    messages.filter(
        (m) => m.type === 'ai' && typeof m.content === 'string' && m.content.trim().length > 0 && !String(m.id).startsWith('streaming-')
    ).length;

/**
 * Decides whether a refetched history proves the interrupted turn completed server-side.
 * True when the refetched history contains more non-empty assistant messages than were
 * displayed when the stream broke.
 */
export const streamRecoveredNewAssistantMessage = (
    previousAssistantCount: number,
    refetchedDisplayMessages: Array<Pick<AgentMsgDisplayType, 'id' | 'type' | 'content'>>
): boolean => countAssistantDisplayMessages(refetchedDisplayMessages) > previousAssistantCount;

interface AIBlockResult {
    displayMessage: AgentMsgDisplayType | null;
    consumedCount: number;
}

/**
 * Processes a block of AI messages starting at the given index.
 * An AI block includes the initial AI message, any tool calls/results, and final AI responses.
 */
function processAIMessageBlock(
    chatHistory: any[],
    startIndex: number,
    t: (key: string) => string
): AIBlockResult {
    const currentMsgData = chatHistory[startIndex];
    const combinedContentElements: string[] = [];
    let finalTimestamp = currentMsgData.createdAt || currentMsgData.updatedAt || new Date().toISOString();
    let aggregatedFiles: UploadedFileAttachment[] = [];
    const pendingToolInteractions = new Map<string, Partial<ToolResultDetail>>();
    let k = startIndex;

    for (; k < chatHistory.length; k++) {
        const iterMsgData = chatHistory[k];

        // Human message ends the current AI block
        if (iterMsgData.type === 'human') {
            break;
        }

        // Aggregate timestamp and files
        finalTimestamp = iterMsgData.createdAt || iterMsgData.updatedAt || finalTimestamp;
        if (iterMsgData.files?.length > 0) {
            aggregatedFiles.push(...iterMsgData.files);
            aggregatedFiles = aggregatedFiles.filter(
                (file, index, self) => index === self.findIndex((f) => f.key === file.key)
            );
        }

        if (iterMsgData.type === 'ai') {
            const shouldBreak = processAIMessage(
                iterMsgData,
                combinedContentElements,
                pendingToolInteractions,
                k,
                startIndex,
                chatHistory
            );
            if (shouldBreak) {
                k++;
                break;
            }
        } else if (iterMsgData.type === 'tool') {
            processToolMessage(iterMsgData, combinedContentElements, pendingToolInteractions);
        }

        // Check for end of block conditions
        if (shouldEndBlock(iterMsgData, k, chatHistory)) {
            if (k < chatHistory.length - 1 && chatHistory[k + 1].type !== 'tool') {
                k++;
                break;
            }
        }
    }

    const consumedCount = k - startIndex;

    if (combinedContentElements.length > 0 || aggregatedFiles.length > 0) {
        return {
            displayMessage: {
                id: currentMsgData._id?.toString() || generateMessageId(),
                type: 'ai',
                content:
                    combinedContentElements.join('\n\n').trim() ||
                    (aggregatedFiles.length > 0
                        ? t('page.medconnectAgent.chatInterface.aiProcessedFiles')
                        : t('page.medconnectAgent.chatInterface.aiProcessing')),
                timestamp: finalTimestamp,
                createdAt: finalTimestamp,
                attachedFiles: aggregatedFiles.length > 0 ? aggregatedFiles : undefined,
            },
            consumedCount,
        };
    }

    return { displayMessage: null, consumedCount };
}

/**
 * Processes an AI message within a block.
 * Returns true if processing should break out of the block.
 */
function processAIMessage(
    iterMsgData: any,
    combinedContentElements: string[],
    pendingToolInteractions: Map<string, Partial<ToolResultDetail>>,
    k: number,
    startIndex: number,
    chatHistory: any[]
): boolean {
    const aiContent = normalizeUtf8(String(iterMsgData.content || '').trim());
    const isAnnouncement = isGenericToolAnnouncement(aiContent);

    if (aiContent && !isAnnouncement) {
        combinedContentElements.push(aiContent);
    }

    if (iterMsgData.tool_calls_details?.length > 0) {
        iterMsgData.tool_calls_details.forEach((call: ToolCallDetail) => {
            const interaction: Partial<ToolResultDetail> = {
                tool_call_id: call.id,
                tool_name: call.name,
                tool_args: call.args,
                result: undefined,
            };
            pendingToolInteractions.set(call.id, interaction);
            combinedContentElements.push(createToolInteractionTag(interaction));
        });
        return false;
    }

    // If this AI message has no tool calls and is not the start of the block
    if (!isAnnouncement && k > startIndex) {
        return true;
    }

    return false;
}

/**
 * Processes a tool result message within a block.
 */
function processToolMessage(
    iterMsgData: any,
    combinedContentElements: string[],
    pendingToolInteractions: Map<string, Partial<ToolResultDetail>>
): void {
    const existingInteraction = pendingToolInteractions.get(iterMsgData.tool_call_id);
    const rawToolResult = iterMsgData.structured_content !== undefined
        ? iterMsgData.structured_content
        : iterMsgData.content;
    const toolResult = typeof rawToolResult === 'string'
        ? normalizeUtf8(rawToolResult)
        : rawToolResult;

    if (existingInteraction) {
        existingInteraction.result = toolResult;
        const updatedIndex = findAndUpdateToolTag(
            combinedContentElements,
            iterMsgData.tool_call_id,
            existingInteraction
        );
        if (updatedIndex === -1) {
            console.warn(
                `[processChatHistory] Tool interaction tag for call_id ${iterMsgData.tool_call_id} not found for update.`
            );
        }
    } else {
        // Orphaned tool result
        const interaction: ToolResultDetail = {
            tool_call_id: iterMsgData.tool_call_id,
            tool_name: iterMsgData.name || 'Unknown Tool',
            tool_args: {},
            result: toolResult,
        };
        combinedContentElements.push(createToolInteractionTag(interaction));
    }
}

/**
 * Determines if the current AI block should end.
 */
function shouldEndBlock(iterMsgData: any, k: number, chatHistory: any[]): boolean {
    if (
        k < chatHistory.length - 1 &&
        iterMsgData.type === 'ai' &&
        (!iterMsgData.tool_calls_details || iterMsgData.tool_calls_details.length === 0)
    ) {
        return true;
    }
    return false;
}

/**
 * Messages to keep before retrying `prompt`: drops the last question if it is `prompt` (and anything
 * after it, e.g. an empty streaming bubble) so the retried question is not shown twice.
 */
export function dropUnansweredQuestion<T extends { type: string; content?: unknown }>(messages: T[], prompt: string): T[] {
  const lastHuman = messages.map((message) => message.type).lastIndexOf('human')
  return lastHuman >= 0 && messages[lastHuman].content === prompt ? messages.slice(0, lastHuman) : messages
}
